'use client';

import { faChevronLeft, faClose, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { useUser } from '@pickleballinc/ui/components/global/UserInfoProvider';
import {
  Panel,
  PanelBody,
  PanelClose,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '@pickleballinc/ui/components/ui/panel';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  useAddNewPlayer,
  useSearchNewPlayer,
} from '@/modules/league/new-components/add-players-components/hooks';
import Input from '@/modules/league/new-components/add-players-components/Input';
import PlayerSearchCard from '@/modules/league/new-components/add-players-components/PlayerSearchCard';
import { PlayerSearchCardSkeleton } from '@/modules/league/new-components/add-players-components/PlayerSearchCardSkeleton';
import { SearchNewPlayer } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';

interface AddPlayerPanelProps {
  params: { slug: string; sessionId: string; matchDateId: string };
}

export function AddPlayersPanel({ params }: AddPlayerPanelProps) {
  const userData = useUser();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const { setLoading } = useLoader();
  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { mutate: searchNewPlayer, data: searchResults, isLoading } = useSearchNewPlayer();
  const { mutate: addNewPlayer } = useAddNewPlayer();

  useEffect(() => {
    if (searchTerm) {
      searchNewPlayer({ leagueSessionID: params.sessionId, searchWord: searchTerm });
    }
  }, [searchTerm, searchNewPlayer, params.sessionId]);

  useEffect(() => {
    const openPanelParam = searchParams?.get('add');
    setOpen(openPanelParam === 'true');
  }, [searchParams]);

  const handlePanelClose = () => {
    if (searchParams) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('add');
      router.replace(`?${newParams.toString()}`, { scroll: false });
    }
    setOpen(false);
    setSearchTerm('');
  };

  const handleAddPlayer = (player: SearchNewPlayer) => {
    handlePanelClose();
    setLoading(true);
    addNewPlayer(
      {
        leagueSessionID: params.sessionId,
        userID: player.UserID,
        matchDateID: params.matchDateId,
        fullName: `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['league_rsvp']);
          toast.success('Player added to the league successfully');
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
          toast.error('Something went wrong and we were unable to add new player.');
        },
      }
    );
  };

  return (
    <Panel open={open} onOpenChange={handlePanelClose}>
      <PanelContent className='bg-white'>
        <PanelHeader>
          <PanelClose asChild>
            <div className='mb-6 flex items-center gap-4 sm:hidden'>
              <FontAwesomeIcon icon={faChevronLeft} />
              <PanelTitle className='text-lg font-semibold text-gray-900'>Add Player</PanelTitle>
            </div>
          </PanelClose>
          <div className='bg-white pb-4'>
            <div className='hidden sm:flex sm:justify-end'>
              <PanelClose asChild>
                <Button
                  variant='secondary'
                  className='fit-content'
                  size='xs'
                  onClick={handlePanelClose}
                >
                  Close
                </Button>
              </PanelClose>
            </div>

            <Input
              label='Search for a Player'
              PrefixIcon={() => (
                <FontAwesomeIcon icon={faSearch} size='sm' className='text-gray-500' />
              )}
              SuffixIcon={() =>
                searchTerm && (
                  <FontAwesomeIcon
                    icon={faClose}
                    size='sm'
                    className='cursor-pointer text-gray-500 hover:text-gray-700'
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                  />
                )
              }
              placeholder='Search by name'
              size={50}
              className='min-w-[200px]'
              inputProps={{
                className: 'focus:ring-leagues-300',
                labelClassName: 'text-left text-sm py-2 focus:ring-leagues-300',
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </PanelHeader>

        <PanelBody className='bg-gray-100'>
          <div className='flex flex-col gap-4 py-4'>
            {searchResults?.length === 0 && !isLoading && searchTerm ? (
              <div className='text-sm text-gray-500'>No players found.</div>
            ) : null}
            {isLoading ? (
              <>
                <PlayerSearchCardSkeleton />
                <PlayerSearchCardSkeleton />
                <PlayerSearchCardSkeleton />
                <PlayerSearchCardSkeleton />
              </>
            ) : null}
            {searchResults?.length > 0 && searchTerm !== '' && (
              <PlayerSearchCard players={searchResults} handleAddPlayer={handleAddPlayer} />
            )}
          </div>
        </PanelBody>
      </PanelContent>
    </Panel>
  );
}
